import { z } from "zod";

export const UuidSchema = z.string().uuid();
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
const Base64Schema = z.string().min(1);
export const JsonObjectSchema = z.record(z.string(), z.unknown());
export const NonEmptyStringSchema = z.string().min(1);
export const IdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);
export const AgentIdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/)
  .refine((value) => !value.includes(".."), "agent id must not contain '..'");

export type Base64 = z.infer<typeof Base64Schema>;
export type AgentId = z.infer<typeof AgentIdSchema>;

const MAX_PATH_LENGTH = 512;
const MAX_SEGMENT_LENGTH = 128;

export class RelativePathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RelativePathError";
  }
}

function reject(message: string): never {
  throw new RelativePathError(message);
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    reject("path segment contains malformed percent encoding");
  }
}

export function normalizeRelativePath(input: string): string {
  if (input.length === 0) reject("path is required");
  if (input.length > MAX_PATH_LENGTH) reject("path is too long");
  if (input.includes("\0")) reject("path contains a NUL byte");
  if (input.includes("\\")) reject("path must use POSIX separators");
  if (input.startsWith("/")) reject("path must be relative");

  const normalized = input.replace(/\/+/g, "/");
  if (normalized.length === 0) reject("path is required");
  if (normalized.length > MAX_PATH_LENGTH) reject("path is too long");
  if (normalized.endsWith("/")) reject("path contains an empty segment");

  const segments = normalized.split("/");
  for (const segment of segments) {
    if (segment === "") reject("path contains an empty segment");
    if (segment === "." || segment === "..") reject("path contains a traversal segment");
    if (segment.length > MAX_SEGMENT_LENGTH) reject("path segment is too long");

    const decoded = decodePathSegment(segment);
    if (decoded.includes("/") || decoded.includes("\\")) {
      reject("path segment contains an encoded separator");
    }
    if (decoded.includes("\0")) reject("path segment contains an encoded NUL byte");
    if (decoded === "." || decoded === "..") {
      reject("path segment contains encoded traversal");
    }
  }

  return normalized;
}

export const RelativePathSchema = z.string().transform((value, ctx) => {
  try {
    return normalizeRelativePath(value);
  } catch (err) {
    ctx.addIssue({
      code: "custom",
      message: err instanceof Error ? err.message : "invalid relative path",
    });
    return z.NEVER;
  }
});
